import { createSelector } from 'reselect';

// Raw passage data selectors
export const getFluencyAssignment = (state) =>
  state.boostFluency.assignment;

export const getFluencyVisited = (state) =>
  state.boostFluency.fluencyVisited;

export const getFluencySuppressionArmed = (state) =>
  state.boostFluency.fluencySuppressionArmed;

export const getPassageContent = (state) =>
  state.boostFluency.content;

// Memoized derived data selectors
export const getPassageChallengeWords = createSelector(
  [getPassageContent],
  (passageContent) => {
    if (!passageContent?.challengeWords) {
      return [];
    }
    return passageContent.challengeWords.map(item => item.word);
  },
);

export const getPassagePages = createSelector(
  [getPassageContent],
  (passageContent) => {
    if (!passageContent?.pages) {
      return [];
    }
    // API returns array [{ pageNumber: 1, text: "..." }, ...]
    // Sort by pageNumber and extract text strings: ["text", "text", "text"]
    return [...passageContent.pages]
      .sort((a, b) => a.pageNumber - b.pageNumber)
      .map(page => page.text);
  },
);

export const getPassageQuestionPrompt = createSelector(
  [getPassageContent],
  (passageContent) => {
    if (!passageContent?.comprehensionQuestion?.text) {
      return '';
    }
    return passageContent.comprehensionQuestion.text;
  },
);

export const getPassageQuestionOptions = createSelector(
  [getPassageContent],
  (passageContent) => {
    if (!passageContent?.comprehensionQuestion?.answerOptions) {
      return [];
    }
    return passageContent.comprehensionQuestion.answerOptions.map(option => option.text);
  },
);

export const getPassageQuestionAnswer = createSelector(
  [getPassageContent],
  (passageContent) => {
    if (!passageContent?.comprehensionQuestion?.answerOptions) {
      return '';
    }
    const correctOption = passageContent.comprehensionQuestion.answerOptions.find(
      option => option.isCorrect === true,
    );
    return correctOption ? correctOption.text : '';
  },
);

export const getPassageTitle = createSelector(
  [getPassageContent],
  (passageContent) => {
    if (!passageContent?.title) {
      return '';
    }
    return passageContent.title;
  },
);

// Shared passage context attached to every Searchlight event.
export const getPassageMetadata = createSelector(
  [getFluencyAssignment],
  (assignment) => {
    const passage = assignment?.passage ?? {};
    return {
      assignmentId: assignment?.assignmentId,
      // Searchlight types passageId as a string; the assignment API returns a number.
      passageId: passage.id != null ? String(passage.id) : undefined,
      passageVersionId: passage.versionId,
      wordCount: passage.wordCount,
      lexile: passage.lexile,
      fleschKincaid: passage.fleschKincaid,
      genre: passage.genre?.toLowerCase(),
      passageGrade: passage.grade,
    };
  },
);

export const getPassageGenre = createSelector(
  [getFluencyAssignment],
  (assignment) => {
    if (!assignment?.passage?.genre) {
      return '';
    }
    return assignment.passage.genre.toLowerCase();
  },
);
